/**
 * Phase 7 PR 1 — `usePersistOnboarding` hook.
 *
 * Bridges the React `/v2/onboarding` flow (which today persists ONLY
 * to localStorage) to the server via a single atomic POST to
 * `/api/context/:userId`. Step 7 calls `persistOnboardingComplete(answers)`
 * at the "Go to my dashboard" CTA; this hook projects the
 * `OnboardingAnswers` shape into the server-key payload, validates
 * every key against the shared FIELD_SPECS allowlist, and fires ONE
 * POST containing journey + PAR-Q + onboarding-completion flags.
 *
 * **Critical contract — NEVER persist the completion flag without the
 * supporting data.** A completion-only write would leave the athlete
 * marked complete on the server but with no journey selected — the
 * LLM coach has no routing signal, the dashboard has no context. This
 * hook always writes the journey value alongside the flag in a single
 * atomic POST.
 *
 * **Why we don't go through `useSettingsPersist.saveFields`:** that
 * hook is shaped for the Settings UI per-field-chip state machine —
 * it silently swallows errors (transitions status to `error`, never
 * throws). Onboarding completion needs hard pass/fail semantics so
 * Step 7 can surface an inline error chip + leave the CTA enabled
 * for retry. We reuse the SAME `validateField` allowlist + the SAME
 * `POST /api/context/:userId` endpoint + the SAME `mergeContext`
 * local-state merge — but with throw-on-failure semantics.
 *
 * Core keys (always, when present):
 *   - `_journey` / `_journey_set_at`   ← Step 1 selection
 *   - `_parq_completed` / `_parq_completed_at` / `_parq_flags` ← Step 3
 *   - `_birth_year` / `_birth_month` / `Age` ← Step 3 (B2 / COPPA)
 *   - `_onboarding_completed` / `_onboarding_completed_at`
 *
 * B108 — THOSE FIVE COMPLETION KEYS ARE CONDITIONAL, and the condition is the
 * screening itself: `parqAnswersComplete(answers.parq)`, every canonical
 * PAR-Q id an explicit boolean. Short of that the payload carries the data it
 * really has (journey, age, body, paces) and says NOTHING about completion —
 * because `_parq_completed: true` with no answers behind it is what let an
 * unscreened athlete reach full-quality authoring with nothing left to re-ask
 * (round-15 security 1). The caller decides what to do about it; the wizard's
 * Step 7 sends the athlete back to the screening step.
 *
 * F-105 — the wizard also collects (and previously DISCARDED) the rest;
 * these now ride the SAME atomic POST, projected to the canonical keys
 * Settings + the plan engine read (all range-guarded so an out-of-range
 * input is dropped, never aborting the completion write):
 *   - Step 4 body metrics → `_weight_kg` / `_weight_unit` / `_height_cm` /
 *     `_height_unit` / `_body_metrics_date`
 *   - Step 5 race result   → `_vdot` + 5 paces + `_pace_race_distance` /
 *     `_pace_race_time` / `_pace_basis_at`
 *   - Step 6 focus         → `Sport` (discipline) / `Goal` (goal event)
 *
 * F-520 — the 3 SP-3 data-gate fields (weekly mileage / experience / injury)
 * are now collected IN the wizard (the in-wizard data beat, before the guardian
 * beat / Welcome), stashed on `answers.trainingData` as the canonical patch, and
 * folded into this SAME atomic completion POST — range-guarded like the F-105
 * projections. Canonical keys `weekly_mileage` / `weekly_mileage_band` /
 * `weekly_mileage_exact` / `experience` / `injuries` (exactly what the first-look
 * collection beat writes), so first-look's `missingTrainingData(ctx)` is empty
 * for a fresh wizard completion and it mounts straight to generating.
 *
 * F5 / CD 2026-07-27 Item 3 — Step 2 location NOW persists, as `_city`. The
 * CD ruling made the typed-city Continue gate conditional on the answer
 * actually landing somewhere ("A, conditional"), so the wizard writes the
 * same canonical key Settings' Location modal writes.
 *
 * B48 (2026-08-29, founder field report) — AND ITS COORDINATES. This header
 * used to say "City STRING only… because this step never obtains coordinates",
 * and that sentence was the bug wearing a rationale: the step DID obtain them
 * (`geocodeCityOnSave` returns `{label, lat, lon}`) and merely discarded them
 * at Continue. Result: every completed wizard wrote `_city` with `_latitude` /
 * `_longitude` null, so weather and the vitamin-D band ran degraded for the
 * life of the account unless the athlete happened to re-enter the city in
 * Settings. Step 2 now carries `answers.locationCoords` and this hook projects
 * the SAME four canonical keys the Settings Location modal writes:
 * `_city` / `_latitude` / `_longitude` / `_vitamin_d_risk`.
 *
 * B438 (2026-10-21) — AND THE NAME. The name step's answer (`displayName`) was
 * collected, shown on the reveal headline and never written. It is now
 * projected to `preferred_name`, the key Settings and the server's name chain
 * read, when it passes the item 12 name rule (rulings 2026-10-21).
 *
 * What this hook still does NOT persist:
 *   - Step 6 branch data (time-goal / health / social) — no canonical key today
 *   - The formal `Name` key — comes from Settings or the Apple/Google sign-in
 *
 * (B446, "Your usual pace" on the health and social paths, is no longer asked at
 * all: ruling 3, 2026-10-22, removed the screen.)
 */

import { useCallback } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { useAthleteContext } from './useAthleteContext';
import { useAuthFetch } from './useAuthFetch';
import type { OnboardingAnswers } from './onboardingReducer';
import { validateField } from '../lib/settingsPersist';
// B108 — the ONE rule for "the screening is complete", shared with the lite
// flow and (next) the Today migration branch.
import { parqAnswersComplete } from '../lib/parqAnswers';
import { PARQ_QUESTIONS } from '../fixtures/onboarding';
import { roundToCityLevel, vitaminDRiskFromLatitude } from '../lib/locationGeocode';
import {
  calcPaces,
  calculateVDOT,
  trainingPaces,
  formatRaceTime,
  type RaceDistance,
  type RaceDistanceKey,
} from '../lib/vdot';
import { MAX_WEEKLY_MILES, decimalPointText } from '../lib/firstPlan/trainingData';

// F-105 — additive projections for the wizard data the flow already collects
// but PR 1 deferred (Step 4 body metrics · Step 5 race/pace · Step 6 discipline
// + goal event). The maps below translate the wizard's answer vocabulary into
// the canonical athlete_context keys Settings + the plan engine already read.

/** Step 6 discipline id → the `Sport` enum value (settingsPersist
 *  DISCIPLINE_VALUES). Only these 5 ids exist in DISCIPLINE_OPTIONS. */
const DISCIPLINE_ID_TO_SPORT: Record<string, string> = {
  road: 'Road',
  track: 'Track',
  trail: 'Trail',
  xc: 'Cross-country',
  multi: 'Mixed',
};

/** Step 5 onboarding race-distance display label → canonical
 *  `_pace_race_distance` key (RaceDistanceKey). Every label maps 1:1; since
 *  ruling 6 (2026-10-22) '3200m / 2 Mile' keys as '3200m', so its time is saved
 *  like the others (before that its pace basis was previewed and dropped). */
const ONBOARDING_LABEL_TO_KEY: Partial<Record<RaceDistance, RaceDistanceKey>> = {
  '800m': '800m',
  '1600m / Mile': '1mile',
  '3200m / 2 Mile': '3200m',
  '5K': '5k',
  '10K': '10k',
  'Half Marathon': 'half_marathon',
  'Marathon': 'marathon',
};

/** Round to 1-decimal precision — parity with the Settings body-metrics
 *  (WeightField) and VDOT (RecalculateModal) 0.1 precision. */
function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

// ── B438 to B444 — the onboarding input rules (rulings 2026-10-21 item 12) ──
//
// ONE place for the numbers the boxes enforce and the payload guards below
// enforce, so "the box limit equals the saved range" is a shared constant, not
// two literals that can drift. Every sentence names the format in one plain
// line (copy register: no dashes, no exclamation marks, never "safe").

/** Item 12 names: letters in any language (accents included), spaces,
 *  apostrophes, hyphens and periods; 1 to 40 characters; no digits, emoji or
 *  other symbols. */
export const ONBOARDING_NAME_MAX_CHARS = 40;
export const ONBOARDING_NAME_SENTENCE =
  'Use letters, spaces, apostrophes, hyphens and periods, up to 40 characters.';
const NAME_CHARS_RE = /^[\p{L}\p{M} '’.-]+$/u;
const HAS_LETTER_RE = /\p{L}/u;

/**
 * The name rule. `value` is the trimmed name; `''` is a skip (an empty box is
 * always allowed). A name outside the rule is refused with the sentence, never
 * trimmed or stripped into a different name.
 */
export function checkOnboardingName(
  raw: string | null | undefined,
): { ok: true; value: string } | { ok: false; error: string } {
  const value = (raw ?? '').trim();
  if (value === '') return { ok: true, value: '' };
  if (
    Array.from(value).length > ONBOARDING_NAME_MAX_CHARS ||
    !NAME_CHARS_RE.test(value) ||
    !HAS_LETTER_RE.test(value)
  ) {
    return { ok: false, error: ONBOARDING_NAME_SENTENCE };
  }
  return { ok: true, value };
}

/** Saved range for body metrics (the payload guards below). The lb range is
 *  the whole-number span that always converts inside 30 to 200 kg. */
export const WEIGHT_KG_RANGE = { min: 30, max: 200 } as const;
export const WEIGHT_LBS_RANGE = { min: 67, max: 440 } as const;
export const HEIGHT_CM_RANGE = { min: 100, max: 250 } as const;
const WEIGHT_SENTENCE = {
  imperial: `Use digits for a weight from ${WEIGHT_LBS_RANGE.min} to ${WEIGHT_LBS_RANGE.max} lbs.`,
  metric: `Use digits for a weight from ${WEIGHT_KG_RANGE.min} to ${WEIGHT_KG_RANGE.max} kg.`,
} as const;
const HEIGHT_CM_SENTENCE = `Use whole digits for a height from ${HEIGHT_CM_RANGE.min} to ${HEIGHT_CM_RANGE.max} cm.`;
// B465 (walk 2026-10-22): there were two imperial sentences, "feet from 3 to 8 and
// inches from 0 to 11" for a badly formed height and "from 3 ft 4 in to 8 ft 2 in"
// for one outside the saved range, and 3 ft 0 in met the second while the first
// said it was fine. One sentence now names both the form and the range.
const HEIGHT_IMPERIAL_SENTENCE = 'Use whole feet and inches, from 3 ft 4 in to 8 ft 2 in.';
const DECIMAL_DIGITS_RE = /^(\d+\.?\d*|\.\d+)$/;
const WHOLE_DIGITS_RE = /^\d+$/;

/** `null` when the weight box is empty or inside the saved range, otherwise the
 *  sentence for this unit. Digits with at most one decimal point. */
export function weightProblem(raw: string | undefined, unit: 'imperial' | 'metric'): string | null {
  // UX M5 — one comma is the decimal point (es-CO, es-ES, pt-BR, fr keypads).
  const text = decimalPointText(raw);
  if (text === '') return null;
  const range = unit === 'metric' ? WEIGHT_KG_RANGE : WEIGHT_LBS_RANGE;
  if (!DECIMAL_DIGITS_RE.test(text)) return WEIGHT_SENTENCE[unit];
  const n = Number(text);
  return n >= range.min && n <= range.max ? null : WEIGHT_SENTENCE[unit];
}

/** `null` when the height boxes are empty or describe a height inside the saved
 *  100 to 250 cm range as whole numbers, otherwise the sentence. */
export function heightProblem(
  h: { ft?: string; inch?: string; cm?: string },
  unit: 'imperial' | 'metric',
): string | null {
  if (unit === 'metric') {
    const cm = (h.cm ?? '').trim();
    if (cm === '') return null;
    if (!WHOLE_DIGITS_RE.test(cm)) return HEIGHT_CM_SENTENCE;
    const n = Number(cm);
    return n >= HEIGHT_CM_RANGE.min && n <= HEIGHT_CM_RANGE.max ? null : HEIGHT_CM_SENTENCE;
  }
  const ft = (h.ft ?? '').trim();
  const inch = (h.inch ?? '').trim();
  if (ft === '' && inch === '') return null;
  if (ft === '' || !WHOLE_DIGITS_RE.test(ft) || (inch !== '' && !WHOLE_DIGITS_RE.test(inch))) {
    return HEIGHT_IMPERIAL_SENTENCE;
  }
  const feet = Number(ft);
  const inches = inch === '' ? 0 : Number(inch);
  if (feet < 3 || feet > 8 || inches > 11) return HEIGHT_IMPERIAL_SENTENCE;
  const cm = Math.round((feet * 12 + inches) * 2.54);
  return cm >= HEIGHT_CM_RANGE.min && cm <= HEIGHT_CM_RANGE.max ? null : HEIGHT_IMPERIAL_SENTENCE;
}

/** The VDOT span the payload keeps below; a race time that lands outside it
 *  cannot set paces, so the step refuses it with a sentence. */
export const VDOT_USABLE_MIN = 20;
export const VDOT_USABLE_MAX = 90;

/**
 * Can this distance and time set paces? Same test the payload applies: a
 * keyed distance needs a VDOT inside the usable span. Every picker distance has
 * a key since ruling 6; the unkeyed branch stays as the defensive fallback for
 * a label the map does not know.
 */
export function raceTimeUsable(label: string | undefined, seconds: number | undefined): boolean {
  if (!label || typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) {
    return false;
  }
  const key = ONBOARDING_LABEL_TO_KEY[label as RaceDistance];
  if (!key) return calcPaces(label as RaceDistance, seconds) !== null;
  const vdot = calculateVDOT(key, seconds);
  return vdot !== null && vdot >= VDOT_USABLE_MIN && vdot <= VDOT_USABLE_MAX;
}

/**
 * B108 — options for the completion write.
 *
 * `completedAt` is the SCREENING date, and it exists because the wizard is not
 * the only caller: a self-heal or migration replays a localStorage blob whose
 * answers may be weeks old, and stamping `_parq_completed_at: <today>` on
 * those falsifies a clinical record (round-15 security 4). Pass the blob's own
 * answered-at to carry it, `null` to omit the key entirely (the POST is a
 * patch, so an omitted key leaves any earlier real date standing), or leave it
 * undefined for the wizard's own path, where the screening IS happening now.
 */
export type OnboardingPersistOpts = {
  markerOnly?: boolean;
  /** `YYYY-MM-DD`, or `null` to omit the screening date. */
  completedAt?: string | null;
};

export interface UsePersistOnboardingResult {
  /**
   * Atomically persist the onboarding answer data + completion flags
   * to the server via `POST /api/context/:userId`. Single transaction;
   * all keys land together or none do. Resolves on success; **rejects
   * on save failure** (caller catches + surfaces an inline error chip
   * + leaves the CTA enabled for retry).
   *
   * B-28 — `opts.markerOnly` narrows the payload to EXACTLY
   * `_onboarding_completed` + `_onboarding_completed_at`, for the Today
   * gate's season-evidence self-heal: that path has season EVIDENCE of a
   * past completion but no answer data and an UNKNOWN screening state, so
   * it must not stamp `_parq_completed(_at)` over a real earlier
   * screening date (the header contract: never invent supporting data).
   * The wizard + localStorage-migration paths never pass it — their
   * payload is byte-identical to before.
   *
   * B108 — `opts.completedAt` carries the screening date for a caller replaying
   * OLD answers (see `OnboardingPersistOpts`). The wizard omits it.
   *
   * Throws when:
   *   - User isn't signed in (`Not signed in`)
   *   - Any validation fails (caller passed malformed answers; this
   *     should never happen for a normally-completed onboarding)
   *   - The server returns non-2xx (`Server returned NNN`; B196 L5/L9 —
   *     carries a `.code` when the body has one, e.g. `age_below_minimum` /
   *     `age_band_required` / `age_review_pending` / `guardian_consent_required`)
   *   - The fetch itself rejects (network failure)
   */
  persistOnboardingComplete: (
    answers: OnboardingAnswers,
    opts?: OnboardingPersistOpts,
  ) => Promise<void>;
}

/**
 * Build the atomic-batch payload from an `OnboardingAnswers` shape.
 * Pure function (no React, no side effects) — separated for unit
 * testing so a test can assert the exact server-key projection
 * without rendering the hook.
 *
 * Exported for the test file; not part of the public hook API.
 */
/** B2 — conservative age from birth month+year: during the birth month
 *  the birthday counts as not-yet-passed (errs one LOWER — the fail-safe
 *  direction for every age gate). Exported for the test file. */
export function deriveAge(birthYear: number, birthMonth: number, now: Date = new Date()): number {
  let age = now.getFullYear() - birthYear;
  if (now.getMonth() + 1 <= birthMonth) age -= 1;
  return age;
}

export function buildOnboardingPayload(
  answers: OnboardingAnswers,
  now: Date = new Date(),
  opts?: OnboardingPersistOpts,
): Record<string, unknown> {
  // YYYY-MM-DD per `kind: 'date'` validation in settingsPersist.
  const today = now.toISOString().slice(0, 10);

  // B-28 — marker-only projection for the season-evidence self-heal:
  // EXACTLY the completion pair, nothing else. In that state the account's
  // PAR-Q screening happened at an unknown earlier time (or via vanilla),
  // so stamping `_parq_completed_at: <today>` would falsify the record.
  if (opts?.markerOnly) {
    return {
      _onboarding_completed: true,
      _onboarding_completed_at: today,
    };
  }
  // B108 — the screening facts, derived once, from the canonical ids only.
  // `completedAt` lets a NON-WIZARD caller (the Today self-heal replays a blob
  // that may be weeks old) carry the date the answers were actually given, or
  // pass `null` to omit the date rather than stamp today over a real earlier
  // screening. Undefined keeps the wizard's own behaviour: the screening is
  // happening now. A malformed date is dropped rather than sent, so a bad
  // value can never fail validation and abort the whole atomic write.
  const parqBag: Record<string, unknown> = (answers.parq ?? {}) as Record<string, unknown>;
  const screeningComplete = parqAnswersComplete(answers.parq);
  const screeningDate =
    opts?.completedAt === undefined
      ? today
      : typeof opts.completedAt === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(opts.completedAt)
        ? opts.completedAt
        : null;

  const payload: Record<string, unknown> = {
    // Journey data — only if the athlete actually selected one. (Step 1
    // requires it, so this should always be set in a completed flow;
    // guarded defensively so the function stays safe for partial
    // localStorage blobs.)
    ...(answers.journey
      ? {
          _journey: answers.journey,
          _journey_set_at: today,
        }
      : {}),
    // B2 (COPPA, founder-ratified 2026-07-04) — birth month+year are the
    // durable source; the derived Age feeds every existing consumer
    // (clinical bands, U16 reflow gate, under-13 consent gate). The
    // derivation is CONSERVATIVE: during the birth month the birthday
    // counts as not-yet-passed, so age errs one lower — the fail-safe
    // direction for every age gate. Guarded (legacy localStorage blobs
    // have no birth fields; Step 3 makes them required going forward).
    ...(typeof answers.birthYear === 'number' && typeof answers.birthMonth === 'number'
      ? {
          _birth_year: answers.birthYear,
          _birth_month: answers.birthMonth,
          Age: deriveAge(answers.birthYear, answers.birthMonth, now),
        }
      : {}),
    // ── B108 — THE COMPLETION KEYS, AND THE ANSWERS THEY STAND FOR ───────
    // All five ride together or none do. `_parq_completed: true` is a
    // CLINICAL claim (the server's resolveParqConservative reads the flags
    // and, seeing none, authors at full quality), and `_onboarding_completed`
    // is what stops anything re-asking — so a payload that cannot show seven
    // explicit booleans says neither. It stays silent instead: the POST is a
    // patch, so omitting a key preserves whatever an earlier real screening
    // wrote, and the caller routes the athlete to the screening step.
    //
    // What this replaces (round-15 security 1 and 2): the pair was stamped
    // unconditionally — "Step 3 forces an answer set before Step 4+, so
    // reaching the completion call implies PAR-Q done", true of the wizard's
    // own path and of nothing else — and `_parq_flags` was included whenever
    // the bag had ANY key, so an empty bag stamped screened-with-no-flags and
    // a legacy camelCase bag wrote `[]` over real flags.
    //
    // EC-4 lives on inside this branch: the flags come from the CANONICAL
    // question ids only, so no non-canonical key can ever become a flag.
    ...(screeningComplete
      ? {
          _parq_completed: true,
          ...(screeningDate ? { _parq_completed_at: screeningDate } : {}),
          _parq_flags: PARQ_QUESTIONS.filter((q) => parqBag[q.key] === true).map(
            (q) => q.key,
          ),
          // Onboarding completion flags (conceptually set last; here they're
          // just additional keys in the same atomic POST). `_onboarding_completed_at`
          // is always TODAY: it is the marker every gate reads, not a clinical
          // record, and an absent value would leave the athlete un-onboarded.
          _onboarding_completed: true,
          _onboarding_completed_at: today,
        }
      : {}),
  };

  // ── F-105 — persist the rest of what the wizard collected ────────────
  // ADDITIVE only (no new collection): body metrics (Step 4), race/pace
  // (Step 5), discipline + goal event (Step 6) — projected to the SAME
  // canonical keys Settings reads. Every derived value is range-guarded to
  // its FIELD_SPECS bounds BEFORE inclusion, so an out-of-range input is
  // silently dropped rather than throwing and aborting the whole atomic
  // completion POST (the journey + flags must always land). The 3 SP-3
  // data-gate fields (mileage / experience / injury) are NOT collected here
  // and are intentionally untouched.

  // B438 — the NAME step's answer. `NameStep` hands `answers.displayName` up
  // and its docblock says "the ROUTE persists, at the atomic completion POST",
  // but no code did: the typed name reached the reveal headline and nothing
  // else, so Today and the coaches never heard it. It rides the key Settings
  // reads and the server's name chain prefers (`context_data.preferred_name`,
  // `api/lib/athlete-name.js` `preferredName`). `''` is the recorded skip and
  // writes NO key (an earlier name, if any, stays); a name outside the item 12
  // rule is not written either, so a stale blob can never save a rejected value.
  const typedName = checkOnboardingName(answers.displayName);
  if (typedName.ok && typedName.value !== '') {
    payload.preferred_name = typedName.value;
  }

  // Step 2 — training location. `answers.location` is already the resolved
  // form: the geocoder's own label when the athlete tapped Check and accepted
  // a match, otherwise the text they typed. Same canonical key the Settings
  // Location modal writes (`_city`, FIELD_SPECS text/200), so onboarding and
  // Settings converge on one value instead of two.
  //
  // Optional by design — Skip clears `location`, and an athlete who never
  // filled it simply omits the key. Trimmed and length-guarded here so the
  // shared `validateField` pass below can never throw and abort the atomic
  // completion POST (same defensive posture as the F-105 projections).
  const cityAnswer = typeof answers.location === 'string' ? answers.location.trim() : '';
  if (cityAnswer) {
    payload._city = cityAnswer.slice(0, 200);

    // ── B48 (2026-08-29) — the coordinates ride with the city ────────────
    // The header above used to say onboarding "never obtains coordinates".
    // That was true of the SAVE, not of the step: `Step2Location` had
    // `{lat, lon}` from the geocoder in hand and dropped them at Continue, so
    // a completed wizard wrote `_city` with `_latitude`/`_longitude` null and
    // every coordinate consumer — weather, the vitamin-D band — ran degraded
    // for the life of the account. Step 2 now carries `answers.locationCoords`
    // and this projects it, so onboarding and the Settings Location modal
    // write the same FOUR canonical keys instead of one and three nulls.
    //
    // ⚖️ `for` MUST still equal the city being saved. The resolve that
    // produces the pair can land after the athlete went Back and typed a
    // different city; on any mismatch the coordinates are dropped and the row
    // degrades to city-only — exactly today's behaviour, never a coordinate
    // pinned to the wrong place.
    //
    // ⚖️ §8.2 — rounded again here. Step 2 rounds at capture and `validateField`
    // rounds at the 0.01 spec below; this is the middle backstop, and it is the
    // one that guarantees the band is derived from the STORED latitude.
    // Range-guarded like every other F-105 projection, so a malformed blob
    // drops the coordinates rather than throwing and aborting the atomic write.
    const coords = answers.locationCoords;
    if (
      coords &&
      typeof coords.for === 'string' &&
      coords.for.trim() === cityAnswer &&
      typeof coords.lat === 'number' &&
      typeof coords.lon === 'number' &&
      Number.isFinite(coords.lat) &&
      Number.isFinite(coords.lon) &&
      coords.lat >= -90 &&
      coords.lat <= 90 &&
      coords.lon >= -180 &&
      coords.lon <= 180
    ) {
      const lat = roundToCityLevel(coords.lat);
      const lon = roundToCityLevel(coords.lon);
      payload._latitude = lat;
      payload._longitude = lon;
      payload._vitamin_d_risk = vitaminDRiskFromLatitude(lat);
    }
  }

  // Step 4 — body metrics. Canonical source-of-truth is kg / cm (vanilla
  // parity); the unit keys carry the display preference. Weight and height
  // are independent (either can be filled alone).
  const bodyUnit = answers.bodyUnit ?? 'imperial';
  let weightKg: number | null = null;
  if (bodyUnit === 'metric') {
    const kg = parseFloat(decimalPointText(String(answers.weightKg ?? '')));
    if (Number.isFinite(kg)) weightKg = round1(kg);
  } else {
    const lbs = parseFloat(decimalPointText(String(answers.weightLbs ?? '')));
    if (Number.isFinite(lbs)) weightKg = round1(lbs * 0.4535924);
  }
  if (weightKg !== null && weightKg >= WEIGHT_KG_RANGE.min && weightKg <= WEIGHT_KG_RANGE.max) {
    payload._weight_kg = weightKg;
    payload._weight_unit = bodyUnit === 'metric' ? 'kg' : 'lbs';
    payload._body_metrics_date = today;
  }

  let heightCm: number | null = null;
  if (bodyUnit === 'metric') {
    const cm = parseInt(String(answers.heightCm ?? ''), 10);
    if (Number.isFinite(cm)) heightCm = cm;
  } else {
    const ft = parseInt(String(answers.heightFt ?? ''), 10);
    const inch = parseInt(String(answers.heightIn ?? ''), 10);
    if (Number.isFinite(ft) || Number.isFinite(inch)) {
      heightCm = Math.round(
        ((Number.isFinite(ft) ? ft : 0) * 12 + (Number.isFinite(inch) ? inch : 0)) * 2.54,
      );
    }
  }
  if (heightCm !== null && heightCm >= HEIGHT_CM_RANGE.min && heightCm <= HEIGHT_CM_RANGE.max) {
    payload._height_cm = heightCm;
    // Wizard bodyUnit ('imperial' | 'metric') matches HEIGHT_UNIT_VALUES.
    payload._height_unit = bodyUnit;
    payload._body_metrics_date = today;
  }

  // Step 6 — discipline → Sport (enum) + goal event → Goal (free text).
  if (answers.discipline && DISCIPLINE_ID_TO_SPORT[answers.discipline]) {
    payload.Sport = DISCIPLINE_ID_TO_SPORT[answers.discipline];
  }
  if (
    typeof answers.goalEvent === 'string' &&
    answers.goalEvent &&
    answers.goalEvent !== 'No specific goal'
  ) {
    payload.Goal = answers.goalEvent;
  }

  // Step 5 — race result → VDOT + the 5 training paces (min/mi, vanilla
  // parity) + the calibration basis. Mirrors the Settings Recalculate modal
  // projection so the plan PACE cells + Settings VDOT populate from day one.
  const raceKey = answers.raceDistance
    ? ONBOARDING_LABEL_TO_KEY[answers.raceDistance as RaceDistance]
    : undefined;
  const seconds = answers.raceTimeSeconds;
  // Ruling 6 — and only a key the client allowlist (`settingsPersist`'s
  // `_pace_race_distance` enum, which `persistOnboardingComplete` validates
  // every payload key against) accepts. A key it refuses would throw there and
  // abort the WHOLE atomic completion, so the pace basis is left out instead,
  // exactly as '3200m / 2 Mile' always was before it had a key.
  if (
    raceKey &&
    typeof seconds === 'number' &&
    seconds > 0 &&
    !answers.skipPace &&
    validateField('_pace_race_distance', raceKey).ok
  ) {
    const vdot = calculateVDOT(raceKey, seconds);
    const paces = vdot !== null ? trainingPaces(vdot, 'mi') : null;
    if (vdot !== null && vdot >= VDOT_USABLE_MIN && vdot <= VDOT_USABLE_MAX && paces) {
      payload._vdot = round1(vdot);
      payload._easy_pace = paces.easy;
      payload._tempo_pace = paces.tempo;
      payload._interval_pace = paces.interval;
      payload._race_pace = paces.race;
      payload._long_run_pace = paces.long;
      payload._pace_race_distance = raceKey;
      payload._pace_race_time = formatRaceTime(seconds);
      payload._pace_basis_at = today;
    }
  }

  // ── F-520 — the SP-3 data-gate answers, collected by the in-wizard data
  // beat and stashed on `answers.trainingData` as the canonical patch
  // (`buildTrainingDataPatch` output). Fold them into the atomic completion
  // POST under the SAME lowercase canonical keys the first-look collection beat
  // writes, so `missingTrainingData(ctx)` is empty for a fresh completion.
  // Range-guarded: an out-of-range `weekly_mileage` drops the whole mileage
  // triple (band/exact ride with it) rather than aborting the atomic write; a
  // present-but-empty experience/injuries string is skipped. The keys bypass
  // the FIELD_SPECS allowlist (they are context passthrough — see the hook's
  // TRAINING_DATA_KEYS guard), so the values are stored as-is.
  const td = answers.trainingData;
  if (td && typeof td === 'object') {
    const bag = td as Record<string, unknown>;
    const wm = bag.weekly_mileage;
    if (typeof wm === 'number' && Number.isFinite(wm) && wm >= 0 && wm <= MAX_WEEKLY_MILES) {
      payload.weekly_mileage = wm;
      if (typeof bag.weekly_mileage_band === 'string' && bag.weekly_mileage_band) {
        payload.weekly_mileage_band = bag.weekly_mileage_band;
      }
      const exact = bag.weekly_mileage_exact;
      if (typeof exact === 'number' && Number.isFinite(exact) && exact > 0) {
        payload.weekly_mileage_exact = exact;
      }
    }
    if (typeof bag.experience === 'string' && bag.experience.trim()) {
      payload.experience = bag.experience;
    }
    if (typeof bag.injuries === 'string' && bag.injuries.trim()) {
      payload.injuries = bag.injuries;
    }
  }

  // B457 (walk 2026-10-22) — the unit the athlete answered the weekly distance
  // in. The beat showed (and the exact box took) miles or kilometres, but no
  // units preference was ever saved, so the plan and Today came back in miles
  // for an athlete who said kilometres. `distance_unit` is the key the readers
  // already use (`resolveDistanceUnit` in `lib/vdot.ts`, Settings'
  // Preferences row, the server's `unit` reads), so writing it needs no new
  // reader. Stored distances stay miles (band ids and `weekly_mileage` are
  // unchanged); this is the display unit only.
  if (answers.distanceUnit === 'mi' || answers.distanceUnit === 'km') {
    payload.distance_unit = answers.distanceUnit;
  }

  return payload;
}

/**
 * F-520 — the canonical training-data keys the in-wizard data beat produces.
 * They are athlete_context passthrough (lowercase, no FIELD_SPECS entry — the
 * server merges arbitrary context keys, exactly as the first-look collection
 * beat's direct POST does), so they bypass the per-field `validateField`
 * allowlist here. Range-guarding already happened in `buildOnboardingPayload`.
 */
const TRAINING_DATA_KEYS = new Set([
  'weekly_mileage',
  'weekly_mileage_band',
  'weekly_mileage_exact',
  'experience',
  'injuries',
]);

export function usePersistOnboarding(): UsePersistOnboardingResult {
  const { user } = useAuthContext();
  const fetchAuthed = useAuthFetch();
  const { mergeContext } = useAthleteContext();

  const persistOnboardingComplete = useCallback(
    async (answers: OnboardingAnswers, opts?: OnboardingPersistOpts) => {
      const payload = buildOnboardingPayload(answers, new Date(), opts);

      // 1. Validate every key against the shared allowlist. Reject
      //    BEFORE firing the network request if any key is invalid
      //    (defensive — a normally-completed onboarding shouldn't
      //    produce invalid values, but a malformed localStorage
      //    blob during migration could).
      const canonicalUpdates: Record<string, unknown> = {};
      for (const [key, rawValue] of Object.entries(payload)) {
        // EC-4 — `_parq_flags` is a machine-constructed string array (the
        // flagged question keys, never user-typed); the field-spec
        // vocabulary has no array kind and the server passes `_`-keys
        // through untouched, so it bypasses the per-field validation.
        if (key === '_parq_flags') {
          canonicalUpdates[key] = Array.isArray(rawValue) ? rawValue : [];
          continue;
        }
        // F-520 — training-data context passthrough (see TRAINING_DATA_KEYS).
        if (TRAINING_DATA_KEYS.has(key)) {
          canonicalUpdates[key] = rawValue;
          continue;
        }
        const validation = validateField(key, rawValue);
        if (!validation.ok) {
          throw new Error(
            `usePersistOnboarding: invalid ${key} — ${validation.error}`,
          );
        }
        canonicalUpdates[key] = validation.value;
      }

      // 2. Auth gate.
      if (!user) {
        throw new Error('Not signed in');
      }

      // 3. Single POST — atomic by construction (one request, one
      //    transaction on the server). B237 — patch-only: send only the
      //    canonical updates. The server always merges a body with no
      //    `context` key (B148 ruling), so a write fired while `raw` is
      //    stale/empty — e.g. the Today onboarding-migration gate running
      //    before the context Provider has finished loading — can no
      //    longer clobber a key the payload doesn't mention (the
      //    destructive-overwrite data-loss bug). Mirrors
      //    `useAthleteContext.mergeContext`. See
      //    critical-bug-context-destructive-overwrite.md.
      const res = await fetchAuthed(
        `/api/context/${encodeURIComponent(user.id)}`,
        {
          method: 'POST',
          body: JSON.stringify({ patch: canonicalUpdates }),
        },
      );
      if (!res.ok) {
        // B196 L5/L9 (spec §4.1, §4.2, §4.5, F8) — carry the server's refusal
        // code (`age_below_minimum` the floor, `age_band_required` the
        // unknown-age wall, `age_review_pending` the review hold, or the
        // existing `guardian_consent_required`) on the thrown Error, same
        // shape as `CodedError`
        // (`lib/roleRefusal.ts`) and `useGenerateMacroPlan`'s season hooks.
        // This hook renders no UI itself; the caller (the onboarding
        // wizard's completion step) reads the code with `lib/planEdit`'s
        // predicates and shows the ratified sentence.
        let code: string | undefined;
        let refusal: string | undefined;
        let field: string | undefined;
        try {
          const body = await res.json();
          if (body && typeof body.code === 'string') code = body.code;
          // B437 / B438 to B444 — the server now refuses a value outside the
          // item 12 rules with `400 { error, code: 'invalid_field', field }`.
          // Its `error` is a plain sentence meant for the athlete, so it rides
          // the thrown Error as the message (the lite flow prints `err.message`)
          // and Step 7 shows it instead of the generic retry. Bounded so a
          // stray body can never put a paragraph on the screen.
          if (
            code === 'invalid_field' &&
            typeof body.error === 'string' &&
            body.error.trim() !== '' &&
            body.error.length <= 200
          ) {
            refusal = body.error.trim();
          }
          if (code === 'invalid_field' && typeof body.field === 'string') field = body.field;
        } catch {
          /* ignore body parse errors */
        }
        const err: Error & { code?: string; field?: string; userMessage?: string } = new Error(
          refusal ?? `Server returned ${res.status}`,
        );
        if (code) err.code = code;
        if (refusal) {
          err.userMessage = refusal;
          if (field) err.field = field;
        }
        throw err;
      }

      // 4. Apply to local React state via the existing hook's deep-merge.
      //    Mirrors the `saveFields` pattern from settingsPersist — same
      //    redundant Supabase upsert via useAthleteContext.persist, same
      //    no-op outcome since the API already wrote the canonical row.
      mergeContext(canonicalUpdates);
    },
    [user, fetchAuthed, mergeContext],
  );

  return { persistOnboardingComplete };
}
